import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import DocumentoFiscal from "../DocumentoFiscal";
import PhoneInput from "../PhoneInput";
import usePaisFiscal, {
	mascararCep,
	regrasVigentes,
} from "../../hooks/usePaisFiscal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const CompanySchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(80, "Nome muito longo")
		.required("Informe o nome"),
	email: Yup.string().email("E-mail inválido"),
});

const somenteDigitos = (valor) => String(valor || "").replace(/\D/g, "");

// Preenche rua, bairro e cidade pelo CEP. Falhou, os campos seguem editáveis.
const buscarCep = async (cep, setFieldValue) => {
	const digitos = somenteDigitos(cep);

	if (digitos.length !== 8) return;

	try {
		const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
		const dados = await resposta.json();

		if (!dados.erro) {
			setFieldValue("address", dados.logradouro || "");
			setFieldValue("province", dados.bairro || "");
			setFieldValue("city", dados.localidade || "");
			setFieldValue("state", dados.uf || "");
		}
	} catch (err) {
		// silencioso de propósito
	}
};

const SITUACOES = [
	{ value: "active", label: "Ativa" },
	{ value: "suspended", label: "Suspensa" },
	{ value: "blocked", label: "Bloqueada" },
];

const CompanyModal = ({ open, onClose, companyId }) => {
	const regras = usePaisFiscal();
	const initialState = {
		name: "",
		email: "",
		phone: "",
		document: "",
		documentCountry: "",
		postalCode: "",
		address: "",
		addressNumber: "",
		addressComplement: "",
		province: "",
		city: "",
		state: "",
		status: "active",
		dueDate: "",
		planId: "",
		adminName: "",
		adminEmail: "",
		adminPassword: "",
	};

	const [company, setCompany] = useState(initialState);
	const [plans, setPlans] = useState([]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/plans");
				setPlans(data);
			} catch (err) {
				toastError(err);
			}
		})();
	}, [open]);

	useEffect(() => {
		(async () => {
			if (!companyId) return;
			try {
				const { data } = await api.get(`/companies/${companyId}`);
				setCompany((anterior) => ({
					...anterior,
					...data,
					email: data.email || "",
					phone: data.phone || "",
					document: data.document || "",
					documentCountry: data.documentCountry || "",
					postalCode: data.postalCode || "",
					address: data.address || "",
					addressNumber: data.addressNumber || "",
					addressComplement: data.addressComplement || "",
					province: data.province || "",
					city: data.city || "",
					state: data.state || "",
					planId: data.planId ? String(data.planId) : "",
					// O input date só aceita AAAA-MM-DD.
					dueDate: data.dueDate ? String(data.dueDate).slice(0, 10) : "",
				}));
			} catch (err) {
				toastError(err);
			}
		})();

		return () => setCompany(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
	};

	const handleSave = async (values) => {
		const payload = {
			...values,
			// Só dígitos: a máscara é para ler na tela. Guardada com pontuação,
			// a busca por documento (que compara dígitos) deixa de encontrar a
			// empresa, e a checagem de duplicado passa a aceitar o mesmo
			// documento duas vezes.
			document: somenteDigitos(values.document),
			postalCode: somenteDigitos(values.postalCode),
			planId: values.planId ? Number(values.planId) : null,
			dueDate: values.dueDate || null,
		};

		try {
			if (companyId) {
				await api.put(`/companies/${companyId}`, payload);
			} else {
				await api.post("/companies", payload);
			}
			toast.success("Empresa salva.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{companyId ? "Editar empresa" : "Nova empresa"}
					</DialogTitle>
				</DialogHeader>
				<Formik
					initialValues={company}
					enableReinitialize
					validationSchema={CompanySchema}
					onSubmit={(values, actions) => {
						handleSave(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="name">Nome da empresa</Label>
								<Field as={Input} id="name" name="name" autoFocus />
								{touched.name && errors.name && (
									<p className="text-xs text-destructive">{errors.name}</p>
								)}
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="email">E-mail</Label>
									<Field as={Input} id="email" name="email" type="email" />
									{touched.email && errors.email && (
										<p className="text-xs text-destructive">{errors.email}</p>
									)}
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="phone">Telefone</Label>
									<PhoneInput
										id="phone"
										name="phone"
										// O telefone de um cliente estrangeiro é do país dele, não do da
										// plataforma.
										paisPadrao={
											regrasVigentes(regras, values.documentCountry).pais
										}
										value={values.phone}
										onChange={(numero) => setFieldValue("phone", numero)}
									/>
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<DocumentoFiscal
										paisSelecionado={values.documentCountry || ""}
										onPaisChange={(p) => setFieldValue("documentCountry", p)}
										valor={values.document}
										onValorChange={(v) => setFieldValue("document", v)}
										erro={touched.document && errors.document}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="dueDate">Vencimento</Label>
									<Field as={Input} id="dueDate" name="dueDate" type="date" />
								</div>
							</div>

							{/* Endereço de cobrança: sem CEP e número o Asaas recusa a
							    cobrança no cartão desta empresa. */}
							<div className="space-y-3 rounded-lg border p-3">
								<div>
									<p className="text-sm font-medium">Endereço de cobrança</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										CEP e número são exigidos pelo Asaas para cobrar no cartão.
									</p>
								</div>

								<div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
									<div className="space-y-1.5">
										<Label htmlFor="postalCode">
											{regrasVigentes(regras, values.documentCountry).rotuloCep}
										</Label>
										<Input
											id="postalCode"
											name="postalCode"
											inputMode="numeric"
											placeholder={
												regrasVigentes(regras, values.documentCountry)
													.exemploCep || "00000-000"
											}
											value={values.postalCode}
											onChange={(e) => {
												const vigente = regrasVigentes(
													regras,
													values.documentCountry,
												);
												const cep = mascararCep(e.target.value, vigente);
												setFieldValue("postalCode", cep);
												// O ViaCEP só conhece CEP brasileiro: fora do Brasil
												// seria uma ida à rede a cada tecla, sempre em vão.
												if (vigente.pais === "BR") buscarCep(cep, setFieldValue);
											}}
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="addressNumber">Número</Label>
										<Field as={Input} id="addressNumber" name="addressNumber" />
									</div>
								</div>

								<div className="grid gap-3 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="address">Endereço</Label>
										<Field as={Input} id="address" name="address" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="addressComplement">Complemento</Label>
										<Field
											as={Input}
											id="addressComplement"
											name="addressComplement"
										/>
									</div>
								</div>

								<div className="grid gap-3 sm:grid-cols-[1fr_1fr_5rem]">
									<div className="space-y-1.5">
										<Label htmlFor="province">Bairro</Label>
										<Field as={Input} id="province" name="province" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="city">Cidade</Label>
										<Field as={Input} id="city" name="city" />
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="state">UF</Label>
										<Field as={Input} id="state" name="state" maxLength={2} />
									</div>
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label>Plano</Label>
									<Select
										value={values.planId}
										onValueChange={(valor) => setFieldValue("planId", valor)}
									>
										<SelectTrigger>
											<SelectValue placeholder="Sem plano" />
										</SelectTrigger>
										<SelectContent>
											{plans.map((plan) => (
												<SelectItem key={plan.id} value={String(plan.id)}>
													{plan.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label>Situação</Label>
									<Select
										value={values.status}
										onValueChange={(valor) => setFieldValue("status", valor)}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{SITUACOES.map((item) => (
												<SelectItem key={item.value} value={item.value}>
													{item.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Só na criação: uma empresa sem admin nasce inacessível. */}
							{!companyId && (
								<div className="space-y-3 rounded-lg border p-3">
									<div>
										<p className="text-sm font-medium">
											Administrador da empresa
										</p>
										<p className="mt-0.5 text-xs text-muted-foreground">
											Quem vai acessar o sistema por esta empresa.
										</p>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="adminName">Nome</Label>
										<Field as={Input} id="adminName" name="adminName" />
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<div className="space-y-1.5">
											<Label htmlFor="adminEmail">E-mail de acesso</Label>
											<Field
												as={Input}
												id="adminEmail"
												name="adminEmail"
												type="email"
											/>
										</div>
										<div className="space-y-1.5">
											<Label htmlFor="adminPassword">Senha</Label>
											<Field
												as={Input}
												id="adminPassword"
												name="adminPassword"
												type="password"
											/>
										</div>
									</div>
								</div>
							)}

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default CompanyModal;
